import { useState, useEffect, useCallback } from 'react';
import vlog from '../../javascript/vlog';

function useHashtagSearch(authenticatedHeadersHash, profileId) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState({
    followed: [], notFollowed: [],
  });

  // Search hashtags function
  const searchHashtags = useCallback(async (term) => {
    if (term.length < 2) {
      setSearchResults({
        followed: [], notFollowed: [],
      });
      return;
    }

    // Strip out any spaces from the search term - hashtags don't have spaces
    const sanitizedTerm = term.replace(/\s+/g, '');

    setIsSearching(true);
    try {
      // First search all hashtags using text search with prefix matching
      const { data: allHashtags, error } = await window.supabaseClient
        .from('hashtags')
        .select()
        // Use ilike for case-insensitive substring matching
        .ilike('name', `%${sanitizedTerm}%`)
        .order('usage_count', { ascending: false })
        .limit(10);
      if (error) {
        vlog.error('Error searching hashtags:', error.message);
        window.Qwoted.showToast({
          toastTitle: 'Search Error',
          toastBody: `Error searching hashtags: ${error.message}`,
          variant: 'danger',
        });
        return;
      }

      if (!allHashtags || allHashtags.length === 0) {
        setSearchResults({
          followed: [], notFollowed: [],
        });
        return;
      }

      // Extract all hashtag IDs to check follow status
      const hashtagIds = allHashtags.map((h) => h.id);

      // Check which hashtags are already followed by querying with the IDs
      const followedUrl = `${window.V2_URL}/match_feed/api/profiles/${profileId}/followed_hashtag_searches?hashtag_ids=${hashtagIds.join(',')}&limit=${hashtagIds.length}`;
      const followedResponse = await fetch(followedUrl, {
        method: 'GET',
        headers: new Headers({
          ...authenticatedHeadersHash,
          'Content-Type': 'application/json',
        }),
      });

      if (!followedResponse.ok) {
        const errorText = await followedResponse.text();
        vlog.error('Error checking follow status:', followedResponse.statusText, errorText);
        return;
      }

      const followedData = await followedResponse.json();

      // Create a map of followed hashtags for quick lookup
      const followedHashtagMap = new Map();
      followedData.data.forEach((h) => {
        followedHashtagMap.set(h.hashtag_id, h);
      });

      // Separate hashtags into followed and notFollowed groups
      const followed = [];
      const notFollowed = [];

      allHashtags.forEach((hashtag) => {
        if (followedHashtagMap.has(hashtag.id)) {
          // This hashtag is already followed
          followed.push(followedHashtagMap.get(hashtag.id));
        } else {
          // This hashtag is not followed
          notFollowed.push({
            hashtag_id: hashtag.id,
            hashtag_name: hashtag.name,
            hashtag_usage_count: hashtag.usage_count,
            follow_status: null, // Not yet followed
          });
        }
      });

      setSearchResults({
        followed, notFollowed,
      });
    } catch (error) {
      vlog.error('Error searching hashtags:', error);
    } finally {
      setIsSearching(false);
    }
  }, [authenticatedHeadersHash, profileId]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        searchHashtags(searchTerm);
      } else {
        setSearchResults({ followed: [], notFollowed: [] });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchHashtags]);

  // Return a function to clear search and potentially trigger a callback
  const clearSearch = (callback) => {
    setSearchTerm('');
    setSearchResults({ followed: [], notFollowed: [] });
    if (callback) callback();
  };

  return {
    searchTerm, setSearchTerm, isSearching, searchResults, searchHashtags, clearSearch,
  };
}

export default useHashtagSearch;
